import cx from "classnames";
import React, { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Container, PageBannerManager } from "@gamedev-web/gamedev-sdk-core";

import * as commonStyles from "../../components/commonStyles/commonStyles.module.css";
import { PageType } from "../../constants";
import { useCalculateAaaStatus } from "../../helpers/aaaStatusHelpers";
import { useFetchCrmData } from "../../helpers/dataFetchingHelpers";
import { useCalculateIdAzureStatus } from "../../helpers/idAzureStatusHelpers";
import { useCalculateIdXboxStatus } from "../../helpers/idXboxStatusHelpers";
import { useCalculatePitchStatus } from "../../helpers/pitchStatusHelpers";
import { useCookieConsent } from "../../helpers/useCookieConsent";
import {
  useIsDevOrStaging,
  useMinPageHeight,
  useScrollToTopOnLoad,
} from "../../helpers/windowHelpers";
import { OnboardingErrorType } from "../../models/errors";
import { ErrorPage } from "../../pages/ErrorPage";
import { getCurrentError, getIsInitialCrmFetchDone } from "../../redux/selectors";
import {
  getOnboardingSignedIn,
  getOnboardingSignInState,
  SignedInState,
} from "../../redux/selectors/authSelectors";
import { useLogPageView } from "../../telemetry";
import { CustomFooter } from "../CustomFooter/CustomFooter";
import { ErrorBanner } from "../ErrorBanner";
import { PrivacyPolicyLink } from "../PrivacyPolicyLink";
import { LoadingSpinner } from "../Spinner";
import { TestConsoleLink } from "../TestConsoleLink/TestConsoleLink";

type OnboardingPageContainerProps = Readonly<{
  children: ReactNode;
  className?: string;
  pageType: PageType | undefined;
}>;

export const OnboardingPageContainer = ({
  children,
  className,
  pageType,
}: OnboardingPageContainerProps) => {
  useFetchCrmData(pageType);
  useCalculateAaaStatus(pageType);
  useCalculateIdXboxStatus(pageType);
  useCalculateIdAzureStatus(pageType);
  useCalculatePitchStatus(pageType);

  const isSignedIn = useSelector(getOnboardingSignedIn);
  const signedInState = useSelector(getOnboardingSignInState);
  const isInitialCrmFetchDone = useSelector(getIsInitialCrmFetchDone);
  const currentError = useSelector(getCurrentError);
  const isDevOrStaging = useIsDevOrStaging();
  const minPageHeight = useMinPageHeight();
  const dispatch = useDispatch();
  // Loading spinner is not shown in Jest environment
  const [loading, setLoading] = useState(global?.process?.env?.JEST_WORKER_ID === undefined);

  useScrollToTopOnLoad();
  useLogPageView();
  useCookieConsent();
  useEffect(() => {
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    // Display error banner if any, only for DEV and Staging environments
    if (isDevOrStaging) {
      if (currentError) {
        PageBannerManager.displayBanner(() => <ErrorBanner />);
      } else {
        PageBannerManager.hideAllBanners();
      }
    }
  }, [currentError, isDevOrStaging]);

  if (currentError?.type === OnboardingErrorType.InitialCrmFetch) {
    return <ErrorPage />;
  }

  const customFooter = (
    <CustomFooter>
      <TestConsoleLink />
      <PrivacyPolicyLink showInPane={true} />
    </CustomFooter>
  );

  const containerClassName = cx(commonStyles.pageContainer, className);
  // Pre-load style that's not included in the CSS file just to avoid flickering while loading.
  const containerStyle = {
    minHeight: minPageHeight,
    visibility: loading ? "hidden" : "visible",
  };
  const containerStyleSpinner = {
    ...containerStyle,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  if (
    signedInState === SignedInState.Unset ||
    (signedInState === SignedInState.SignedIn && pageType !== undefined && !isInitialCrmFetchDone)
  ) {
    return (
      <>
        <Container className={containerClassName} style={containerStyleSpinner}>
          <LoadingSpinner />
        </Container>
        {isSignedIn && customFooter}
      </>
    );
  }

  return (
    <>
      <Container className={containerClassName} style={containerStyle}>
        {children}
      </Container>
      {isSignedIn && customFooter}
    </>
  );
};
